/**
 * Keep every moving item fresh: poll each transient one until it settles, from the moment it
 * appears — after an upload, and after a page reload that finds a book still ingesting or a
 * paper still being written. One loop per set of moving ids; the loop drops an item the moment
 * it settles, so a mocked no-op sleep asks exactly once per state. The rules say what "moving"
 * means for the item's kind.
 *
 * It never gives up silently: there is no time cap, only a back-off (the item's own interval
 * for ten minutes, then 15 s, then 30 s after twenty); a blip (status 0 or ≥ 500) is asked
 * again rather than dropped; `get` and `replace` are read through refs, so a renewed token is
 * used on the next poll; and a tab coming back into view is polled at once, since a background
 * tab's timers are throttled to about one a minute.
 */
import { useEffect, useRef } from "react";
import type { ApiResult } from "../api/client";
import { transient } from "../states/Page";

export type Rules<T> = { moving: (item: T) => boolean; interval: (item: T) => number };

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const MIN = 60_000;

/** The item's own interval for ten minutes, then 15 s, then 30 s from twenty minutes on. */
export function backedOff(base: number, ageMs: number): number {
  if (ageMs < 10 * MIN) return base;
  if (ageMs < 20 * MIN) return Math.max(base, 15_000);
  return Math.max(base, 30_000);
}

export function useTransientPolling<T extends { id: string }>(
  items: T[] | null,
  get: (id: string) => Promise<ApiResult<T>>,
  replace: (item: T) => void,
  rules: Rules<T>,
  sleep: (ms: number) => Promise<void> = realSleep,
  clock: () => number = Date.now,
): void {
  const getRef = useRef(get);
  const replaceRef = useRef(replace);
  getRef.current = get;
  replaceRef.current = replace;
  const moving = (items ?? []).filter(rules.moving);
  const key = moving.map((d) => d.id).join(",");
  useEffect(() => {
    if (!key) return;
    let live = true;
    const pending = new Map(moving.map((d) => [d.id, d]));
    const seen = new Map(moving.map((d) => [d.id, clock()]));
    // Back in view: cut the current wait short.
    let wake: () => void = () => {};
    const onVisible = () => {
      if (document.visibilityState === "visible") wake();
    };
    document.addEventListener("visibilitychange", onVisible);
    void (async () => {
      while (live && pending.size > 0) {
        const wait = Math.min(
          ...[...pending.values()].map((d) =>
            backedOff(rules.interval(d), clock() - (seen.get(d.id) ?? clock())),
          ),
        );
        await Promise.race([sleep(wait), new Promise<void>((r) => (wake = r))]);
        if (!live) return;
        for (const id of [...pending.keys()]) {
          const r = await getRef.current(id);
          if (!live) return;
          if (!r.ok) {
            // A blip is asked again at the backed-off interval; a 404 (deleted) or any
            // other 4xx is an answer, and the item is dropped.
            if (!transient(r)) pending.delete(id);
            continue;
          }
          replaceRef.current(r.data);
          if (rules.moving(r.data)) pending.set(id, r.data);
          else pending.delete(id);
        }
      }
    })();
    return () => {
      live = false;
      document.removeEventListener("visibilitychange", onVisible);
    };
    // The set of moving ids is the dependency; a status change that keeps an item
    // moving must not restart the loop's sleep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
